import { useState, useEffect, useCallback, useMemo } from "react";
import { API, useAuth } from "@/App";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Progress } from "../ui/progress";
import { 
  Plus, FolderKanban, Table2, Calendar, GanttChart, ChevronRight, ChevronDown,
  MoreHorizontal, User, Clock, DollarSign, AlertTriangle, CheckCircle2,
  MessageSquare, Paperclip, Send, Play, Pause, Flag, Shield, Building2,
  Zap, Users, FileText, Trash2, Edit, Copy, ArrowRight, RefreshCw, GripVertical,
  Mail, Phone, UserPlus, AlertCircle, ClipboardList
} from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import SiteSurveyPanel from "./SiteSurveyPanel";
import ContractorQuickAddDialog from "./ContractorQuickAddDialog";
import { ExpandButton, ExpandedEditorDialog } from "./ExpandedEditorDialog";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import ProjectStakeholderUpdate from "./ProjectStakeholderUpdate";
import { Camera } from "lucide-react";

// Status configuration with colors
const STATUS_CONFIG = {
  upcoming: { label: "Upcoming", color: "#3b82f6", bg: "#dbeafe", icon: Clock },
  in_progress: { label: "In Progress", color: "#f59e0b", bg: "#fef3c7", icon: Play },
  on_hold: { label: "On Hold", color: "#8b5cf6", bg: "#ede9fe", icon: Pause },
  completed: { label: "Completed", color: "#10b981", bg: "#d1fae5", icon: CheckCircle2 },
  stuck: { label: "Stuck", color: "#ef4444", bg: "#fee2e2", icon: AlertTriangle },
  review: { label: "In Review", color: "#06b6d4", bg: "#cffafe", icon: FileText }
};

const RISK_LEVELS = {
  low: { label: "Low", color: "#10b981" },
  medium: { label: "Medium", color: "#f59e0b" },
  high: { label: "High", color: "#ef4444" },
  critical: { label: "Critical", color: "#dc2626" }
};

export default function ProjectCommandCenter({ embedded = false }) {
  const { token, user } = useAuth();
  const canEditSchedule = ["super_admin", "hub_admin", "project_manager"].includes(user?.role);
  const [activeView, setActiveView] = useState("table");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [templates, setTemplates] = useState({});
  const [progressNotes, setProgressNotes] = useState([]);
  const [pmNotes, setPmNotes] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [newNoteType, setNewNoteType] = useState("pm_note");
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [quickAddFor, setQuickAddFor] = useState(null);   // phase id | "new_phase"
  const [editor, setEditor] = useState(null);             // { title, subtitle, value, save }
  
  // Dialogs
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewPhase, setShowNewPhase] = useState(false);
  const [showPhaseDetail, setShowPhaseDetail] = useState(null);
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);
  const [costItems, setCostItems] = useState([]);
  const [costSaving, setCostSaving] = useState(false);
  const [proposals, setProposals] = useState([]);
  
  // Form states
  const [newProject, setNewProject] = useState({
    name: "", description: "", budget: 0, project_type: "construction",
    hospital_id: "", hospital_name: "", facility_id: "", facility_name: "",
    start_date: "", target_end_date: "", priority: "medium",
    scope_of_work: "", contact_name: "", contact_email: "", contact_phone: "",
    assigned_contractor_id: "", assigned_contractor_name: "",
    funding_source: "", square_footage: "", department: "",
    compliance_requirements: [],
    team_members: [],
    cost_line_items: []
  });
  const [newPhase, setNewPhase] = useState({ 
    name: "", description: "", scope_notes: "", status: "upcoming", lead_time_weeks: 1, 
    budget: 0, risk_level: "low", compliance_tags: [],
    start_date: "", end_date: "",
    owner_id: "", owner_name: "",
    assigned_contractors: [], assigned_suppliers: [],
    compliance_steps: [],
    infection_prevention: { icra_class: "", precautions: [], barriers: "", monitoring: "" }
  });
  const [newUpdate, setNewUpdate] = useState("");

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API}/project-command/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch single project with phases
  const fetchProject = useCallback(async (projectId) => {
    try {
      const response = await fetch(`${API}/project-command/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedProject(data.project);
        setPhases(data.phases || []);
        fetchProgressNotes(projectId);
      }
    } catch (error) {
      console.error("Failed to fetch project:", error);
    }
  }, [token]);

  // Fetch contractors
  const fetchContractors = useCallback(async () => {
    try {
      const response = await fetch(`${API}/project-command/contractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContractors(data.contractors || []);
      }
    } catch (error) {
      console.error("Failed to fetch contractors:", error);
    }
  }, [token]);

  // Fetch health systems
  const fetchHealthSystems = useCallback(async () => {
    try {
      const response = await fetch(`${API}/health-systems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHealthSystems(data.health_systems || []);
      }
    } catch (error) {
      console.error("Failed to fetch health systems:", error);
    }
  }, [token]);

  // Fetch facilities for selected health system
  const fetchFacilities = useCallback(async (hospitalId) => {
    if (!hospitalId) { setFacilities([]); return; }
    try {
      const response = await fetch(`${API}/health-systems/${hospitalId}/facilities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFacilities(data.facilities || []);
      }
    } catch (error) {
      console.error("Failed to fetch facilities:", error);
    }
  }, [token]);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/users?role=supplier`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
  }, [token]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/project-command/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || {});
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
    fetchContractors();
    fetchHealthSystems();
    fetchSuppliers();
    fetchTemplates();
  }, [fetchProjects, fetchContractors, fetchHealthSystems, fetchSuppliers, fetchTemplates]);

  // Create project
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      const payload = {
        ...newProject,
        budget: parseFloat(newProject.budget) || 0,
        square_footage: newProject.square_footage ? parseFloat(newProject.square_footage) : null,
      };
      const response = await fetch(`${API}/project-command/projects`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("Project created successfully");
        fetchProjects();
        setShowNewProject(false);
        setNewProject({
          name: "", description: "", budget: 0, project_type: "construction",
          hospital_id: "", hospital_name: "", facility_id: "", facility_name: "",
          start_date: "", target_end_date: "", priority: "medium",
          scope_of_work: "", contact_name: "", contact_email: "", contact_phone: "",
          assigned_contractor_id: "", assigned_contractor_name: "",
          funding_source: "", square_footage: "", department: "",
          compliance_requirements: [],
          team_members: []
        });
        // Auto-select the new project
        if (data.project?.id) {
          fetchProject(data.project.id);
        }
      }
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  // Create from template
  const handleCreateFromTemplate = async (templateKey) => {
    try {
      const response = await fetch(
        `${API}/project-command/projects/from-template?template_key=${templateKey}&project_name=${templates[templateKey]?.name || "New Project"}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Project created with ${data.phases?.length || 0} phases`);
        fetchProjects();
        setShowTemplateSelect(false);
        // Auto-select the new project
        setSelectedProject(data.project);
        setPhases(data.phases || []);
      }
    } catch (error) {
      toast.error("Failed to create project from template");
    }
  };

  // Create phase
  const handleCreatePhase = async () => {
    if (!selectedProject) return;
    try {
      const response = await fetch(`${API}/project-command/phases`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...newPhase,
          project_id: selectedProject.id,
          order: phases.length
        })
      });
      if (response.ok) {
        toast.success("Phase created");
        fetchProject(selectedProject.id);
        setShowNewPhase(false);
        setNewPhase({ 
          name: "", description: "", scope_notes: "", status: "upcoming", lead_time_weeks: 1, 
          budget: 0, risk_level: "low", compliance_tags: [],
          start_date: "", end_date: "",
          owner_id: "", owner_name: "",
          assigned_contractors: [], assigned_suppliers: [],
          compliance_steps: [],
          infection_prevention: { icra_class: "", precautions: [], barriers: "", monitoring: "" }
        });
      }
    } catch (error) {
      toast.error("Failed to create phase");
    }
  };

  // Update phase status
  const handleUpdatePhaseStatus = async (phaseId, newStatus) => {
    try {
      const response = await fetch(`${API}/project-command/phases/${phaseId}`, {
        method: "PATCH",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success("Status updated");
        fetchProject(selectedProject.id);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Update phase owner
  const handleAssignContractor = async (phaseId, contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    try {
      const response = await fetch(`${API}/project-command/phases/${phaseId}`, {
        method: "PATCH",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ 
          owner_id: contractorId,
          owner_name: contractor?.company_name || contractor?.name
        })
      });
      if (response.ok) {
        toast.success("Contractor assigned");
        fetchProject(selectedProject.id);
      }
    } catch (error) {
      toast.error("Failed to assign contractor");
    }
  };

  // Assign a contractor record (already in hand) as the phase owner
  const handleAssignContractorRecord = async (phaseId, contractor) => {
    try {
      const response = await fetch(`${API}/project-command/phases/${phaseId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          owner_id: contractor.id,
          owner_name: contractor.company_name || contractor.name
        })
      });
      if (!response.ok) throw new Error("assign failed");
      toast.success(`${contractor.company_name || contractor.name} assigned to the phase`);
      fetchProject(selectedProject.id);
    } catch (error) {
      toast.error("Contractor added but could not be assigned — pick them in the Owner list");
    }
  };

  // Patch a phase inline (timeline edits: dates and duration)
  const patchPhase = async (phaseId, fields) => {
    try {
      const response = await fetch(`${API}/project-command/phases/${phaseId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(fields)
      });
      if (!response.ok) throw new Error("patch failed");
      setPhases(prev => prev.map(p => (p.id === phaseId ? { ...p, ...fields } : p)));
      toast.success("Timeline updated");
    } catch (error) {
      toast.error("Could not update the timeline");
    }
  };

  // Add update/comment
  const handleAddUpdate = async (phaseId) => {
    if (!newUpdate.trim()) return;
    try {
      const response = await fetch(`${API}/project-command/phases/${phaseId}/updates`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ content: newUpdate, update_type: "comment" })
      });
      if (response.ok) {
        toast.success("Update added");
        setNewUpdate("");
        fetchProject(selectedProject.id);
      }
    } catch (error) {
      toast.error("Failed to add update");
    }
  };

  // Toggle phase expansion
  const togglePhaseExpand = (phaseId) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  // Fetch progress notes
  const fetchProgressNotes = async (projectId) => {
    try {
      const response = await fetch(`${API}/project-command/projects/${projectId}/progress-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProgressNotes(data.notes || []);
      }
    } catch (error) {
      console.error("Failed to fetch progress notes:", error);
    }
  };

  // Add progress note
  const handleAddProgressNote = async () => {
    if (!newNoteContent.trim() || !selectedProject) return;
    try {
      const response = await fetch(`${API}/project-command/projects/${selectedProject.id}/progress-notes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ content: newNoteContent, note_type: newNoteType })
      });
      if (response.ok) {
        toast.success("Note added");
        setNewNoteContent("");
        fetchProgressNotes(selectedProject.id);
      }
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Update phase scope inline
  const handleUpdatePhaseScope = async (phaseId, scopeData) => {
    try {
      await fetch(`${API}/project-command/projects/${selectedProject.id}/phases/${phaseId}/scope`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(scopeData)
      });
    } catch (error) {
      console.error("Failed to update phase scope:", error);
    }
  };

  // Send progress report email
  const handleSendProgressReport = async () => {
    if (!selectedProject) return;
    const teamEmails = (selectedProject.team_members || []).map(m => m.email).filter(Boolean);
    if (teamEmails.length === 0) {
      toast.error("No team members with email addresses to send to");
      return;
    }
    setSendingReport(true);
    try {
      const response = await fetch(`${API}/project-command/projects/${selectedProject.id}/send-progress-report`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_emails: teamEmails, pm_notes: pmNotes })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Report sent to ${data.recipients?.length || 0} stakeholders`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send report");
      }
    } catch (error) {
      toast.error("Failed to send progress report");
    } finally {
      setSendingReport(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.upcoming;
    const Icon = config.icon;
    return (
      <Badge 
        style={{ backgroundColor: config.bg, color: config.color, border: `1px solid ${config.color}` }}
        className="flex items-center gap-1"
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Risk badge component
  const RiskBadge = ({ level }) => {
    const config = RISK_LEVELS[level] || RISK_LEVELS.low;
    return (
      <Badge variant="outline" style={{ borderColor: config.color, color: config.color }}>
        <Flag className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Render Table View
  const renderTableView = () => (
    <div className="space-y-2">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-slate-100 rounded-lg text-sm font-medium text-slate-600">
        <div className="col-span-3">Phase</div>
        <div className="col-span-2">Owner</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1">Lead Time</div>
        <div className="col-span-1">Budget</div>
        <div className="col-span-1">Progress</div>
        <div className="col-span-1">Risk</div>
        <div className="col-span-1">Actions</div>
      </div>
      
      {/* Phase Rows */}
      {phases.map((phase, index) => (
        <div key={phase.id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
          {/* Main Row */}
          <div 
            className="grid grid-cols-12 gap-4 px-4 py-3 items-center cursor-pointer"
            onClick={() => togglePhaseExpand(phase.id)}
          >
            <div className="col-span-3 flex items-center gap-2">
              {expandedPhases[phase.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="font-medium">{phase.name}</span>
              {phase.compliance_tags?.length > 0 && (
                <Shield className="w-4 h-4 text-blue-500" title={phase.compliance_tags.join(", ")} />
              )}
            </div>
            <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
              <Select
                value={phase.owner_id || ""}
                onValueChange={(value) => {
                  if (value === "__add_contractor__") { setQuickAddFor(phase.id); return; }
                  handleAssignContractor(phase.id, value);
                }}
              >
                <SelectTrigger className="h-8" data-testid={`phase-owner-select-${phase.id}`}>
                  <SelectValue placeholder="Assign..." />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || c.name}
                      {c.documents_pending && " — docs pending"}
                    </SelectItem>
                  ))}
                  <SelectItem value="__add_contractor__" className="text-orange-600 font-semibold"
                    data-testid={`phase-owner-add-new-${phase.id}`}>
                    + Add new contractor
                  </SelectItem>
                </SelectContent>
              </Select>
              {phase.owner_id && contractors.find(c => c.id === phase.owner_id)?.documents_pending && (
                <p className="text-[10px] font-bold text-amber-600 mt-0.5"
                  data-testid={`phase-owner-docs-pending-${phase.id}`}>
                  Documents pending
                </p>
              )}
            </div>
            <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
              <Select
                value={phase.status}
                onValueChange={(value) => handleUpdatePhaseStatus(phase.id, value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue>
                    <StatusBadge status={phase.status} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(STATUS_CONFIG).map(status => (
                    <SelectItem key={status} value={status}>
                      <StatusBadge status={status} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1 text-sm text-slate-600">
              <Clock className="w-3 h-3 inline mr-1" />
              {phase.lead_time_weeks}w
            </div>
            <div className="col-span-1 text-sm text-slate-600">
              <DollarSign className="w-3 h-3 inline" />
              {(phase.budget || 0).toLocaleString()}
            </div>
            <div className="col-span-1">
              <div className="flex items-center gap-2">
                <Progress value={phase.percent_complete || 0} className="h-2 flex-1" />
                <span className="text-xs text-slate-500">{phase.percent_complete || 0}%</span>
              </div>
            </div>
            <div className="col-span-1">
              <RiskBadge level={phase.risk_level} />
            </div>
            <div className="col-span-1 flex gap-1">
              <Button 
                variant="ghost" 
                size="sm"
                data-testid={`phase-actions-${phase.id}`}
                onClick={(e) => { e.stopPropagation(); setShowPhaseDetail(phase); }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Expanded Content */}
          {expandedPhases[phase.id] && (
            <div className="px-4 pb-4 border-t bg-slate-50">
              <div className="grid grid-cols-2 gap-4 mt-3">
                {/* Left: Details */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-slate-700">Details</h4>
                  {phase.description && (
                    <p className="text-sm text-slate-600">{phase.description}</p>
                  )}
                  {phase.compliance_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {phase.compliance_tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {phase.owner_details && (
                    <div className="text-sm">
                      <span className="text-slate-500">Assigned to: </span>
                      <span className="font-medium">{phase.owner_details.company_name}</span>
                      {phase.owner_details.phone && (
                        <span className="text-slate-400 ml-2">{phase.owner_details.phone}</span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Right: Updates Feed */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-slate-700 flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    Updates ({phase.updates?.length || 0})
                  </h4>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {(phase.updates || []).slice(-3).map(update => (
                      <div key={update.id} className="bg-white p-2 rounded text-sm border">
                        <p>{update.content}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {update.author_name} • {new Date(update.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Add update..." 
                      value={newUpdate}
                      onChange={(e) => setNewUpdate(e.target.value)}
                      className="flex-1 h-8 text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddUpdate(phase.id)}
                    />
                    <Button size="sm" onClick={() => handleAddUpdate(phase.id)}>
                      <Send className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      
      {phases.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No phases yet. Add your first phase to get started.</p>
        </div>
      )}
    </div>
  );

  // Draggable Phase Card Component for Kanban
  const DraggablePhaseCard = ({ phase, isDragging }) => {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
      id: phase.id,
      data: { phase }
    });
    
    const style = transform ? {
      transform: CSS.Translate.toString(transform),
      opacity: isDragging ? 0.5 : 1
    } : undefined;

    return (
      <Card 
        ref={setNodeRef}
        style={style}
        className={`cursor-grab active:cursor-grabbing transition-shadow ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : 'hover:shadow-md'}`}
        data-testid={`kanban-card-${phase.id}`}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between">
            <p className="font-medium text-sm flex-1">{phase.name}</p>
            <div {...attributes} {...listeners} className="p-1 hover:bg-slate-100 rounded cursor-grab">
              <GripVertical className="w-4 h-4 text-slate-400" />
            </div>
          </div>
          {phase.owner_name && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <User className="w-3 h-3" />
              {phase.owner_name}
            </div>
          )}
          {phase.compliance_tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {phase.compliance_tags.slice(0, 2).map(tag => (
                <Badge key={tag} variant="outline" className="text-xs py-0">
                  {tag}
                </Badge>
              ))}
              {phase.compliance_tags.length > 2 && (
                <Badge variant="outline" className="text-xs py-0">+{phase.compliance_tags.length - 2}</Badge>
              )}
            </div>
          )}
          <div className="flex items-center justify-between">
            <RiskBadge level={phase.risk_level} />
            <span className="text-xs text-slate-400">{phase.percent_complete || 0}%</span>
          </div>
          <Progress value={phase.percent_complete || 0} className="h-1" />
          <div className="flex items-center justify-between text-xs text-slate-400 pt-1 border-t">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {phase.lead_time_weeks}w
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {(phase.budget || 0).toLocaleString()}
            </span>
            <button 
              onClick={(e) => { e.stopPropagation(); setShowPhaseDetail(phase); }}
              className="p-1 hover:bg-slate-200 rounded"
            >
              <MoreHorizontal className="w-3 h-3" />
            </button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Droppable Column Component for Kanban
  const DroppableColumn = ({ status, children, count }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: status,
      data: { status }
    });
    
    const Icon = STATUS_CONFIG[status].icon;

    return (
      <div className="flex-shrink-0 w-80" data-testid={`kanban-column-${status}`}>
        <div 
          className="rounded-t-lg px-3 py-2 font-medium text-sm flex items-center justify-between"
          style={{ backgroundColor: STATUS_CONFIG[status].bg, color: STATUS_CONFIG[status].color }}
        >
          <span className="flex items-center gap-1">
            <Icon className="w-4 h-4" />
            {STATUS_CONFIG[status].label}
          </span>
          <Badge variant="secondary" className="text-xs">{count}</Badge>
        </div>
        <div 
          ref={setNodeRef}
          className={`bg-slate-100 rounded-b-lg p-2 min-h-[450px] space-y-2 transition-colors ${isOver ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
        >
          {children}
          {count === 0 && !isOver && (
            <div className="text-center py-8 text-slate-400 text-sm">
              Drop phases here
            </div>
          )}
          {isOver && (
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center text-blue-500 text-sm animate-pulse">
              Drop to change status
            </div>
          )}
        </div>
      </div>
    );
  };

  // Drag Overlay Card (shown while dragging)
  const DragOverlayCard = ({ phase }) => (
    <Card className="w-72 shadow-2xl ring-2 ring-blue-500 opacity-90">
      <CardContent className="p-3 space-y-2">
        <p className="font-medium text-sm">{phase.name}</p>
        {phase.owner_name && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <User className="w-3 h-3" />
            {phase.owner_name}
          </div>
        )}
        <div className="flex items-center justify-between">
          <RiskBadge level={phase.risk_level} />
          <span className="text-xs text-slate-400">{phase.percent_complete || 0}%</span>
        </div>
        <Progress value={phase.percent_complete || 0} className="h-1" />
      </CardContent>
    </Card>
  );

  // Kanban drag state
  const [activeKanbanPhase, setActiveKanbanPhase] = useState(null);

  const kanbanSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor)
  );

  const handleKanbanDragStart = (event) => {
    const { active } = event;
    const phase = phases.find(p => p.id === active.id);
    setActiveKanbanPhase(phase);
  };

  const handleKanbanDragEnd = async (event) => {
    const { active, over } = event;
    setActiveKanbanPhase(null);

    if (!over) return;

    const phaseId = active.id;
    const newStatus = over.id;
    const phase = phases.find(p => p.id === phaseId);

    if (phase && phase.status !== newStatus && STATUS_CONFIG[newStatus]) {
      // Optimistic update
      setPhases(prev => prev.map(p => 
        p.id === phaseId ? { ...p, status: newStatus } : p
      ));

      // Update backend
      try {
        const response = await fetch(`${API}/project-command/phases/${phaseId}`, {
          method: "PATCH",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ status: newStatus })
        });
        
        if (response.ok) {
          toast.success(`Phase moved to ${STATUS_CONFIG[newStatus].label}`);
        } else {
          // Revert on failure
          setPhases(prev => prev.map(p => 
            p.id === phaseId ? { ...p, status: phase.status } : p
          ));
          toast.error("Failed to update status");
        }
      } catch (error) {
        // Revert on error
        setPhases(prev => prev.map(p => 
          p.id === phaseId ? { ...p, status: phase.status } : p
        ));
        toast.error("Failed to update status");
      }
    }
  };

  // Render Kanban View with Drag & Drop
  const renderKanbanView = () => {
    const kanbanColumns = Object.keys(STATUS_CONFIG);
    const phasesByStatus = {};
    kanbanColumns.forEach(status => {
      phasesByStatus[status] = phases.filter(p => p.status === status);
    });

    return (
      <DndContext 
        sensors={kanbanSensors}
        collisionDetection={closestCorners}
        onDragStart={handleKanbanDragStart}
        onDragEnd={handleKanbanDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {kanbanColumns.map(status => (
            <DroppableColumn 
              key={status} 
              status={status} 
              count={phasesByStatus[status]?.length || 0}
            >
              {phasesByStatus[status]?.map(phase => (
                <DraggablePhaseCard 
                  key={phase.id} 
                  phase={phase} 
                  isDragging={activeKanbanPhase?.id === phase.id}
                />
              ))}
            </DroppableColumn>
          ))}
        </div>
        <DragOverlay>
          {activeKanbanPhase && <DragOverlayCard phase={activeKanbanPhase} />}
        </DragOverlay>
      </DndContext>
    );
  };

  // Render Timeline/Gantt View
  const renderTimelineView = () => {
    const today = new Date();
    const projectStart = phases[0]?.start_date ? new Date(phases[0].start_date) : today;
    const totalWeeks = phases.reduce((sum, p) => sum + (p.lead_time_weeks || 1), 0);
    
    return (
      <div className="space-y-4">
        {/* Timeline Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-100 rounded-lg">
          <span className="font-medium text-sm text-slate-600">Project Timeline ({totalWeeks} weeks total)</span>
          <div className="flex gap-2">
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <div key={key} className="flex items-center gap-1 text-xs">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: config.color }} />
                {config.label}
              </div>
            ))}
          </div>
        </div>
        
        {/* Edit permission note */}
        <p className="text-xs text-slate-500 px-1" data-testid="timeline-edit-note">
          {canEditSchedule
            ? "Dates and duration are editable here — changes save as you leave each field."
            : "Read-only. A super admin or project manager can edit the schedule."}
        </p>

        {/* Timeline Bars */}
        <div className="space-y-2">
          {phases.map((phase, index) => {
            const startOffset = phases.slice(0, index).reduce((sum, p) => sum + (p.lead_time_weeks || 1), 0);
            const widthPercent = ((phase.lead_time_weeks || 1) / totalWeeks) * 100;
            const leftPercent = (startOffset / totalWeeks) * 100;
            const statusConfig = STATUS_CONFIG[phase.status] || STATUS_CONFIG.upcoming;

            return (
              <div key={phase.id} className="flex flex-wrap items-center gap-3 border border-slate-100 rounded-lg p-2 bg-white"
                data-testid={`timeline-row-${phase.id}`}>
                <div className="w-40 text-sm font-medium truncate" title={phase.name}>{phase.name}</div>
                <div className="flex-1 min-w-[160px] h-8 bg-slate-100 rounded relative">
                  <div
                    className="absolute h-full rounded flex items-center px-2 text-xs text-white font-medium"
                    style={{
                      left: `${leftPercent}%`,
                      width: `${widthPercent}%`,
                      backgroundColor: statusConfig.color,
                      minWidth: '40px'
                    }}
                    title={`${phase.lead_time_weeks} weeks`}
                  >
                    {phase.lead_time_weeks}w
                  </div>
                </div>
                {canEditSchedule ? (
                  <div className="flex items-center gap-2">
                    <input type="date" defaultValue={phase.start_date || ""}
                      className="h-8 w-[132px] rounded-md border border-slate-300 px-2 text-xs"
                      onBlur={(e) => e.target.value !== (phase.start_date || "") && patchPhase(phase.id, { start_date: e.target.value })}
                      data-testid={`timeline-start-${phase.id}`} />
                    <span className="text-slate-400 text-xs">to</span>
                    <input type="date" defaultValue={phase.end_date || ""}
                      className="h-8 w-[132px] rounded-md border border-slate-300 px-2 text-xs"
                      onBlur={(e) => e.target.value !== (phase.end_date || "") && patchPhase(phase.id, { end_date: e.target.value })}
                      data-testid={`timeline-end-${phase.id}`} />
                    <input type="number" min="1" defaultValue={phase.lead_time_weeks || 1}
                      className="h-8 w-16 rounded-md border border-slate-300 px-2 text-xs"
                      onBlur={(e) => {
                        const wks = parseInt(e.target.value, 10);
                        if (wks > 0 && wks !== (phase.lead_time_weeks || 1)) patchPhase(phase.id, { lead_time_weeks: wks });
                      }}
                      data-testid={`timeline-weeks-${phase.id}`} />
                    <span className="text-slate-400 text-xs">wks</span>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 w-[280px]">
                    {phase.start_date || "TBD"} {phase.end_date ? `— ${phase.end_date}` : ""} · {phase.lead_time_weeks || 1} wks
                  </div>
                )}
                <div className="w-28"><StatusBadge status={phase.status} /></div>
              </div>
            );
          })}
          {phases.length === 0 && (
            <p className="text-slate-400 text-center py-8 text-sm">No phases on this project yet.</p>
          )}
        </div>
      </div>
    );
  };

  // Progress Report View
  const renderCostWorksheet = () => {
    if (!selectedProject) return null;

    // Load cost items from project on first render of this tab
    const items = costItems.length > 0 ? costItems : (selectedProject.cost_line_items || []);
    if (costItems.length === 0 && selectedProject.cost_line_items?.length > 0) {
      setCostItems([...selectedProject.cost_line_items]);
    }

    const updateItem = (idx, field, value) => {
      const updated = [...items];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === "retail_price" || field === "subcontractor_cost") {
        const retail = field === "retail_price" ? (parseFloat(value) || 0) : (updated[idx].retail_price || 0);
        const sub = field === "subcontractor_cost" ? (parseFloat(value) || 0) : (updated[idx].subcontractor_cost || 0);
        updated[idx].projected_margin = retail - sub;
      }
      setCostItems(updated);
    };

    const addItem = (includedAbove = false) => {
      setCostItems([...items, { phase: "", retail_price: 0, subcontractor_cost: 0, projected_margin: 0, contractor_team: "", included_above: includedAbove }]);
    };

    const removeItem = (idx) => setCostItems(items.filter((_, i) => i !== idx));

    const saveCostWorksheet = async () => {
      setCostSaving(true);
      try {
        const res = await fetch(`${API}/project-command/projects/${selectedProject.id}/cost-worksheet`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ cost_line_items: items })
        });
        if (res.ok) {
          toast.success("Cost worksheet saved");
          selectedProject.cost_line_items = items;
        } else {
          toast.error("Failed to save cost worksheet");
        }
      } catch (e) { toast.error("Error saving"); }
      setCostSaving(false);
    };

    const fetchProposals = async () => {
      try {
        const res = await fetch(`${API}/project-command/projects/${selectedProject.id}/proposals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProposals(data.proposals || []);
        }
      } catch (e) { /* ignore */ }
    };

    const importProposal = async (proposalId) => {
      try {
        const res = await fetch(`${API}/project-command/proposals/${proposalId}/import`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          toast.success("Proposal imported into cost worksheet");
          fetchProjects();
          fetchProposals();
          setCostItems([]);
        } else {
          toast.error("Failed to import proposal");
        }
      } catch (e) { toast.error("Error importing"); }
    };

    const totalRetail = items.filter(i => !i.included_above).reduce((s, i) => s + (i.retail_price || 0), 0);
    const totalSub = items.filter(i => !i.included_above).reduce((s, i) => s + (i.subcontractor_cost || 0), 0);
    const totalMargin = items.filter(i => !i.included_above).reduce((s, i) => s + (i.projected_margin || 0), 0);
    const marginPct = totalRetail > 0 ? ((totalMargin / totalRetail) * 100).toFixed(1) : 0;

    return (
      <div className="space-y-6" data-testid="cost-worksheet-view">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Retail (Client Quote)</p>
            <p className="text-2xl font-bold text-slate-800">${totalRetail.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Subcontractor Cost</p>
            <p className="text-2xl font-bold text-slate-800">${totalSub.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Projected Margin</p>
            <p className="text-2xl font-bold text-emerald-600">${totalMargin.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Margin %</p>
            <p className="text-2xl font-bold text-blue-600">{marginPct}%</p>
          </div>
        </div>

        {/* Cost Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <DollarSign className="w-4 h-4" /> Cost Breakdown
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => addItem(false)}>
                <Plus className="w-4 h-4 mr-1" /> Add Line Item
              </Button>
              <Button variant="outline" size="sm" onClick={() => addItem(true)}>
                <Plus className="w-4 h-4 mr-1" /> Included Above
              </Button>
              <Button size="sm" onClick={saveCostWorksheet} disabled={costSaving} data-testid="save-cost-worksheet">
                {costSaving ? "Saving..." : "Save Worksheet"}
              </Button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No cost items yet</p>
              <p className="text-sm">Click "Add Line Item" to start building the cost worksheet</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="cost-detail-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-1/3">Phase / Scope</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-600">Retail Price</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-600">Sub Cost</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-600">Margin</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Contractor / Team</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className={`border-b last:border-0 hover:bg-blue-50/30 ${item.included_above ? "bg-slate-50/50" : ""}`}>
                    <td className="px-4 py-2">
                      <Input value={item.phase} onChange={(e) => updateItem(idx, "phase", e.target.value)} placeholder="e.g., Plumbing Materials & Labor" className="h-8 text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      {item.included_above ? (
                        <span className="text-xs text-slate-400 italic block text-right">Included above</span>
                      ) : (
                        <Input type="number" value={item.retail_price || ""} onChange={(e) => updateItem(idx, "retail_price", parseFloat(e.target.value) || 0)} placeholder="0" className="h-8 text-sm text-right" />
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {item.included_above ? (
                        <span className="text-xs text-slate-400 block text-right">—</span>
                      ) : (
                        <Input type="number" value={item.subcontractor_cost || ""} onChange={(e) => updateItem(idx, "subcontractor_cost", parseFloat(e.target.value) || 0)} placeholder="0" className="h-8 text-sm text-right" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {item.included_above ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <span className={`font-semibold ${(item.projected_margin || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          ${(item.projected_margin || 0).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Input value={item.contractor_team || ""} onChange={(e) => updateItem(idx, "contractor_team", e.target.value)} placeholder="e.g., RDX Plumbing" className="h-8 text-sm" />
                    </td>
                    <td className="px-1 py-2">
                      <button onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-50 border-t-2 border-blue-200">
                  <td className="px-4 py-3 font-bold text-slate-700">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-700">${totalRetail.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-700">${totalSub.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-600">${totalMargin.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Supplier Proposals Section */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Supplier Proposals
            </h3>
            <Button variant="outline" size="sm" onClick={fetchProposals}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>

          {proposals.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No proposals submitted yet. Suppliers can submit proposals through their portal.</p>
            </div>
          ) : (
            <div className="divide-y">
              {proposals.map((p) => (
                <div key={p.id} className="p-4 hover:bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-slate-800">{p.supplier_company}</span>
                      <span className="text-slate-400 mx-2">|</span>
                      <span className="text-sm text-slate-500">{p.supplier_name} ({p.supplier_email})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        p.status === "submitted" ? "bg-amber-100 text-amber-700" :
                        p.status === "imported" ? "bg-green-100 text-green-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>{p.status}</span>
                      <span className="font-semibold text-blue-600">${(p.total_proposed || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  {p.cost_line_items?.length > 0 && (
                    <div className="bg-slate-50 rounded p-3 mb-2">
                      <table className="w-full text-xs">
                        <thead><tr className="text-slate-500"><th className="text-left py-1">Phase</th><th className="text-right py-1">Proposed Cost</th></tr></thead>
                        <tbody>
                          {p.cost_line_items.map((item, i) => (
                            <tr key={i} className="border-t border-slate-200">
                              <td className="py-1 text-slate-700">{item.phase}</td>
                              <td className="py-1 text-right text-slate-700">{item.included_above ? "Included" : `$${(item.proposed_cost || 0).toLocaleString()}`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {p.notes && <p className="text-sm text-slate-500 mb-2">Note: {p.notes}</p>}
                  {p.status === "submitted" && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-green-600 border-green-200" onClick={() => importProposal(p.id)}>
                        <Plus className="w-3 h-3 mr-1" /> Import to Worksheet
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProgressReport = () => {
    const STATUS_COLORS = {
      completed: "bg-emerald-500", in_progress: "bg-blue-500",
      upcoming: "bg-slate-400", on_hold: "bg-amber-500", stuck: "bg-red-500"
    };
    const NOTE_TYPES = {
      pm_note: { label: "PM Note", color: "bg-blue-100 text-blue-700" },
      hospital_request: { label: "Hospital Request", color: "bg-emerald-100 text-emerald-700" },
      supplier_update: { label: "Supplier Update", color: "bg-amber-100 text-amber-700" },
      action_item: { label: "Action Item", color: "bg-red-100 text-red-700" },
      scope_change: { label: "Scope Change", color: "bg-violet-100 text-violet-700" }
    };

    return (
      <div className="space-y-6" data-testid="progress-report-view">
        {/* Report Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <img 
                src="/images/sterilemate-logo.jpg" 
                alt="SterileMate" 
                className="h-12 w-auto object-contain rounded bg-white/10 p-1"
                data-testid="progress-report-logo"
              />
              <div>
              <h2 className="text-xl font-bold">{selectedProject?.name}</h2>
              <p className="text-slate-300 text-sm mt-1">
                {selectedProject?.hospital_name} {selectedProject?.facility_name ? `— ${selectedProject.facility_name}` : ""}
              </p>
              {selectedProject?.department && <p className="text-slate-400 text-xs mt-1">Department: {selectedProject.department}</p>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => openAuthedDoc(
                  `/project-command/projects/${selectedProject.id}/progress-report/document`,
                  { token, fmt: "pdf", pm_notes: pmNotes })}
                data-testid="progress-report-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSendProgressReport}
                disabled={sendingReport}
                data-testid="send-progress-report-btn"
              >
                <Send className="w-4 h-4 mr-1" /> {sendingReport ? "Sending..." : "Email to Stakeholders"}
              </Button>
            </div>
          </div>

          {/* Team summary */}
          {selectedProject?.team_members?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {selectedProject.team_members.map((m, i) => (
                <span key={i} className={`text-xs px-2.5 py-1 rounded-full ${
                  m.type === "contractor" ? "bg-blue-500/20 text-blue-200" :
                  m.type === "supplier" ? "bg-amber-500/20 text-amber-200" :
                  "bg-emerald-500/20 text-emerald-200"
                }`}>
                  {m.name} ({m.role})
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Schedule Summary Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="text-left p-3 font-semibold w-[22%]">Task</th>
                    <th className="text-left p-3 font-semibold w-[15%]">Owner</th>
                    <th className="text-left p-3 font-semibold w-[12%]">Due Date</th>
                    <th className="text-left p-3 font-semibold">Status / Scope Details</th>
                  </tr>
                </thead>
                <tbody>
                  {phases.map((phase, idx) => {
                    const owners = [phase.owner_name, ...(phase.assigned_contractors || []).map(c => c.company_name || c.name)].filter(Boolean);
                    const dateStr = phase.start_date && phase.end_date 
                      ? `${phase.start_date} — ${phase.end_date}` 
                      : phase.start_date ? `From ${phase.start_date}` 
                      : phase.lead_time_weeks ? `${phase.lead_time_weeks} wks` : "TBD";
                    
                    return (
                      <tr key={phase.id} className={`border-b border-slate-100 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`} data-testid={`report-phase-${phase.id}`}>
                        <td className="p-3 font-semibold text-slate-800 align-top border-r border-slate-100">
                          {phase.name}
                        </td>
                        <td className="p-3 text-slate-600 align-top border-r border-slate-100 text-xs">
                          {owners.length > 0 ? owners.join(" / ") : <span className="text-slate-400 italic">Unassigned</span>}
                          {(phase.assigned_suppliers || []).length > 0 && (
                            <div className="mt-1 text-amber-600">{phase.assigned_suppliers.map(s => s.company_name || s.name).join(", ")}</div>
                          )}
                        </td>
                        <td className="p-3 text-slate-600 align-top border-r border-slate-100 text-xs">{dateStr}</td>
                        <td className="p-3 align-top">
                          {/* Status badge */}
                          <span className={`inline-block text-white text-xs px-2.5 py-0.5 rounded-full font-semibold ${STATUS_COLORS[phase.status] || STATUS_COLORS.upcoming}`}>
                            {(phase.status || "upcoming").replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          
                          {/* Description */}
                          {phase.description && <p className="text-slate-600 text-xs mt-2">{phase.description}</p>}
                          
                          {/* Scope Notes — editable */}
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Scope &amp; progress notes
                              </span>
                              <ExpandButton testId={`expand-scope-${phase.id}`} onClick={() => setEditor({
                                title: phase.name,
                                subtitle: "Scope and progress notes — appears in the report and the emailed update",
                                value: phase.scope_notes || "",
                                placeholder: "Document what was done, what is next, measurements, deliverables, who was on site…",
                                save: (text) => {
                                  handleUpdatePhaseScope(phase.id, { scope_notes: text });
                                  setPhases(prev => prev.map(p => (p.id === phase.id ? { ...p, scope_notes: text } : p)));
                                },
                              })} />
                            </div>
                            <textarea
                              key={`${phase.id}-${(phase.scope_notes || "").length}`}
                              className="w-full text-xs border border-slate-200 rounded-md p-2 bg-slate-50 focus:bg-white focus:border-blue-300 focus:ring-1 focus:ring-blue-200 transition-colors min-h-[72px] resize-y"
                              defaultValue={phase.scope_notes || ""}
                              placeholder="Add scope notes, progress details, deliverables..."
                              data-testid={`scope-notes-${phase.id}`}
                              onBlur={(e) => {
                                if (e.target.value !== (phase.scope_notes || "")) {
                                  handleUpdatePhaseScope(phase.id, { scope_notes: e.target.value });
                                  setPhases(prev => prev.map(p => (p.id === phase.id ? { ...p, scope_notes: e.target.value } : p)));
                                }
                              }}
                            />
                          </div>
                          
                          {/* Compliance tags */}
                          {phase.compliance_tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {phase.compliance_tags.map(tag => (
                                <span key={tag} className="bg-blue-50 text-blue-700 text-[10px] px-2 py-0.5 rounded-full border border-blue-200">{tag}</span>
                              ))}
                            </div>
                          )}
                          
                          {/* Infection Prevention */}
                          {phase.infection_prevention?.icra_class && phase.infection_prevention.icra_class !== "none" && (
                            <div className="mt-2 text-xs bg-red-50 border border-red-200 rounded-md p-2 text-red-800">
                              <strong>ICRA {phase.infection_prevention.icra_class.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</strong>
                              {phase.infection_prevention.barriers && ` | Barriers: ${phase.infection_prevention.barriers}`}
                              {phase.infection_prevention.precautions?.length > 0 && ` | ${phase.infection_prevention.precautions.join(", ")}`}
                            </div>
                          )}
                          
                          {/* Compliance Steps */}
                          {phase.compliance_steps?.length > 0 && (
                            <div className="mt-2 text-xs space-y-1">
                              {phase.compliance_steps.map((s, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${s.status === "complete" ? "text-emerald-500" : "text-slate-300"}`} />
                                  <span className="text-slate-600">{s.step}</span>
                                  {s.responsible && <span className="text-slate-400">— {s.responsible}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* PM Notes for Email */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> PM Notes (included in the report and the email)
              </h3>
              <ExpandButton testId="expand-pm-notes" onClick={() => setEditor({
                title: "Project manager notes",
                subtitle: `${selectedProject?.name || "Project"} — printed in the PDF and the emailed report`,
                value: pmNotes,
                placeholder: "Overall status, decisions made, what the facility needs to action, look-ahead…",
                save: (text) => setPmNotes(text),
              })} />
            </div>
            <Textarea
              value={pmNotes}
              onChange={(e) => setPmNotes(e.target.value)}
              placeholder="Add project manager notes here — these will be included at the bottom of the emailed progress report..."
              rows={6}
              className="text-sm"
              data-testid="pm-notes-textarea"
            />
          </CardContent>
        </Card>

        {/* Notes & Communication Thread */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Notes & Communication
            </h3>
            
            {/* Add Note */}
            <div className="mb-4 space-y-2">
              <div className="flex items-center gap-2">
                <Select value={newNoteType} onValueChange={setNewNoteType}>
                  <SelectTrigger className="w-44" data-testid="progress-note-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(NOTE_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex-1" />
                <ExpandButton testId="expand-progress-note" onClick={() => setEditor({
                  title: NOTE_TYPES[newNoteType]?.label || "Note",
                  subtitle: `${selectedProject?.name || "Project"} — written into the communication thread`,
                  value: newNoteContent,
                  placeholder: "Document it properly — what happened, who was involved, what is needed and by when…",
                  save: (text) => setNewNoteContent(text),
                })} />
              </div>
              <Textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Add a note, request, or action item..."
                rows={4}
                className="text-sm"
                data-testid="progress-note-input"
              />
              <div className="flex justify-end">
                <Button onClick={handleAddProgressNote} size="sm" data-testid="add-note-btn">
                  <Send className="w-4 h-4 mr-1" /> Post
                </Button>
              </div>
            </div>
            
            {/* Notes list */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {progressNotes.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">No notes yet. Add a note above to start the project communication thread.</p>
              ) : (
                progressNotes.map(note => {
                  const noteConfig = NOTE_TYPES[note.note_type] || NOTE_TYPES.pm_note;
                  return (
                    <div key={note.id} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${noteConfig.color}`}>{noteConfig.label}</Badge>
                        <span className="text-xs font-medium text-slate-700">{note.author_name}</span>
                        <span className="text-xs text-slate-400">{new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                        {note.request_response_from?.length > 0 && (
                          <Badge variant="outline" className="text-xs border-red-300 text-red-600">Response Requested</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      {/* Responses */}
                      {note.responses?.length > 0 && (
                        <div className="mt-2 ml-4 space-y-2 border-l-2 border-slate-200 pl-3">
                          {note.responses.map(r => (
                            <div key={r.id} className="text-xs">
                              <span className="font-medium text-slate-700">{r.author_name}</span>
                              <span className="text-slate-400 ml-1">{new Date(r.created_at).toLocaleDateString()}</span>
                              <p className="text-slate-600 mt-0.5">{r.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${embedded ? '' : 'p-6'}`} data-testid="project-command-center">
      {/* Header - hidden when embedded */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FolderKanban className="w-7 h-7 text-blue-600" />
              Project Command Center
            </h1>
            <p className="text-slate-500 mt-1">Healthcare Construction Project Management</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTemplateSelect(true)}>
              <Copy className="w-4 h-4 mr-2" />
              From Template
            </Button>
            <Button onClick={() => setShowNewProject(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        </div>
      )}

      {/* Action buttons when embedded */}
      {embedded && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setShowTemplateSelect(true)}>
            <Copy className="w-4 h-4 mr-2" />
            From Template
          </Button>
          <Button onClick={() => setShowNewProject(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </div>
      )}

      {/* Project Selection */}
      {!selectedProject ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(project => (
            <Card 
              key={project.id} 
              className="cursor-pointer hover:border-blue-300 transition-colors"
              onClick={() => fetchProject(project.id)}
              data-testid={`project-card-${project.id}`}
            >
              <CardHeader>
                <CardTitle className="text-lg">{project.name}</CardTitle>
                <CardDescription>{project.description || "No description"}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-slate-500">
                      {project.phase_counts?.total || 0} phases
                    </span>
                    {project.phase_counts?.stuck > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {project.phase_counts.stuck} stuck
                      </Badge>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
          
          {projects.length === 0 && (
            <Card className="col-span-full py-12 text-center">
              <CardContent>
                <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500 mb-4">No projects yet. Create your first project to get started.</p>
                <div className="flex justify-center gap-2">
                  <Button variant="outline" onClick={() => setShowTemplateSelect(true)}>
                    <Copy className="w-4 h-4 mr-2" />
                    Use Template
                  </Button>
                  <Button onClick={() => setShowNewProject(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Blank
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <>
          {/* Project Header */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)}>
                    ← Back
                  </Button>
                  <div>
                    <h2 className="text-xl font-bold">{selectedProject.name}</h2>
                    <p className="text-sm text-slate-500">{selectedProject.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-slate-500">
                    <span className="font-medium text-slate-700">{phases.length}</span> phases
                  </div>
                  <Button onClick={() => setShowNewPhase(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Phase
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* View Switcher */}
          <Tabs value={activeView} onValueChange={setActiveView}>
            <TabsList className="flex w-full justify-start overflow-x-auto sm:w-fit">
              <TabsTrigger value="table" className="flex items-center gap-1">
                <Table2 className="w-4 h-4" />
                Table
              </TabsTrigger>
              <TabsTrigger value="kanban" className="flex items-center gap-1">
                <FolderKanban className="w-4 h-4" />
                Kanban
              </TabsTrigger>
              <TabsTrigger value="timeline" className="flex items-center gap-1">
                <GanttChart className="w-4 h-4" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="costs" className="flex items-center gap-1" data-testid="costs-tab">
                <DollarSign className="w-4 h-4" />
                Cost Worksheet
              </TabsTrigger>
              <TabsTrigger value="report" className="flex items-center gap-1">
                <ClipboardList className="w-4 h-4" />
                Progress Report
              </TabsTrigger>
              <TabsTrigger value="survey" className="flex items-center gap-1" data-testid="survey-tab">
                <Camera className="w-4 h-4" />
                Site Survey
              </TabsTrigger>
              <TabsTrigger value="stakeholder-update" className="flex items-center gap-1" data-testid="stakeholder-update-tab">
                <Mail className="w-4 h-4" />
                Stakeholder Update
              </TabsTrigger>
            </TabsList>

            <TabsContent value="table" className="mt-4">
              {renderTableView()}
            </TabsContent>
            <TabsContent value="kanban" className="mt-4">
              {renderKanbanView()}
            </TabsContent>
            <TabsContent value="timeline" className="mt-4">
              {renderTimelineView()}
            </TabsContent>
            <TabsContent value="costs" className="mt-4">
              {renderCostWorksheet()}
            </TabsContent>
            <TabsContent value="report" className="mt-4">
              {renderProgressReport()}
            </TabsContent>
            <TabsContent value="survey" className="mt-4">
              <SiteSurveyPanel linkType="project" linkId={selectedProject.id} token={token}
                label={selectedProject.name} />
            </TabsContent>
            <TabsContent value="stakeholder-update" className="mt-4">
              <ProjectStakeholderUpdate projectId={selectedProject.id} token={token}
                projectName={selectedProject.name} />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* New Capital Project Intake Form */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Building2 className="w-5 h-5 text-blue-600" />
              New Capital Project Intake
            </DialogTitle>
            <DialogDescription>Enter all project details for a new healthcare capital project</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Section 1: Project Info */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Project Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Project Name *</Label>
                  <Input
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder="e.g., LBJ Hospital SPD Renovation"
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <Label>Project Type</Label>
                  <Select value={newProject.project_type} onValueChange={(v) => setNewProject({ ...newProject, project_type: v })}>
                    <SelectTrigger data-testid="project-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="construction">New Construction</SelectItem>
                      <SelectItem value="renovation">Renovation</SelectItem>
                      <SelectItem value="expansion">Expansion</SelectItem>
                      <SelectItem value="equipment_install">Equipment Installation</SelectItem>
                      <SelectItem value="compliance_upgrade">Compliance Upgrade</SelectItem>
                      <SelectItem value="water_purification">Water Purification</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newProject.priority} onValueChange={(v) => setNewProject({ ...newProject, priority: v })}>
                    <SelectTrigger data-testid="project-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Department</Label>
                  <Input
                    value={newProject.department}
                    onChange={(e) => setNewProject({ ...newProject, department: e.target.value })}
                    placeholder="e.g., Sterile Processing, Dialysis"
                  />
                </div>
                <div>
                  <Label>Square Footage</Label>
                  <Input
                    type="number"
                    value={newProject.square_footage}
                    onChange={(e) => setNewProject({ ...newProject, square_footage: e.target.value })}
                    placeholder="e.g., 5000"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    placeholder="Brief project overview..."
                    rows={2}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Scope of Work</Label>
                  <Textarea
                    value={newProject.scope_of_work}
                    onChange={(e) => setNewProject({ ...newProject, scope_of_work: e.target.value })}
                    placeholder="Detailed scope: demolition, plumbing, electrical, equipment, etc."
                    rows={3}
                    data-testid="project-scope-input"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Location */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Location
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Health System</Label>
                  <Select
                    value={newProject.hospital_id}
                    onValueChange={(v) => {
                      const hs = healthSystems.find(h => h.id === v);
                      setNewProject({ ...newProject, hospital_id: v, hospital_name: hs?.name || "", facility_id: "", facility_name: "" });
                      fetchFacilities(v);
                    }}
                  >
                    <SelectTrigger data-testid="project-hospital-select"><SelectValue placeholder="Select health system" /></SelectTrigger>
                    <SelectContent>
                      {healthSystems.map(hs => (
                        <SelectItem key={hs.id} value={hs.id}>{hs.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Facility</Label>
                  <Select
                    value={newProject.facility_id}
                    onValueChange={(v) => {
                      const fac = facilities.find(f => f.id === v);
                      setNewProject({ ...newProject, facility_id: v, facility_name: fac?.name || "" });
                    }}
                  >
                    <SelectTrigger data-testid="project-facility-select"><SelectValue placeholder="Select facility" /></SelectTrigger>
                    <SelectContent>
                      {facilities.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 3: Timeline & Budget */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Timeline & Budget
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={newProject.start_date}
                    onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                    data-testid="project-start-date"
                  />
                </div>
                <div>
                  <Label>Target End Date</Label>
                  <Input
                    type="date"
                    value={newProject.target_end_date}
                    onChange={(e) => setNewProject({ ...newProject, target_end_date: e.target.value })}
                    data-testid="project-end-date"
                  />
                </div>
                <div>
                  <Label>Budget ($)</Label>
                  <Input
                    type="number"
                    value={newProject.budget}
                    onChange={(e) => setNewProject({ ...newProject, budget: e.target.value })}
                    placeholder="0.00"
                    data-testid="project-budget-input"
                  />
                </div>
                <div>
                  <Label>Funding Source</Label>
                  <Select value={newProject.funding_source} onValueChange={(v) => setNewProject({ ...newProject, funding_source: v })}>
                    <SelectTrigger><SelectValue placeholder="Select funding source" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="capital_budget">Capital Budget</SelectItem>
                      <SelectItem value="grant">Grant</SelectItem>
                      <SelectItem value="bond">Bond</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 4: Project Cost Worksheet */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Project Cost Worksheet
              </h3>
              <p className="text-xs text-slate-400 mb-3">Add line items to build a detailed cost vs. retail breakdown. Margins are calculated automatically.</p>

              {/* Cost Line Items Table */}
              {newProject.cost_line_items.length > 0 && (
                <div className="border rounded-lg overflow-hidden mb-3">
                  <table className="w-full text-sm" data-testid="cost-worksheet-table">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Phase / Scope</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-600">Retail Price</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-600">Sub Cost</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-600">Margin</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Contractor</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {newProject.cost_line_items.map((item, idx) => (
                        <tr key={idx} className="border-b last:border-0 hover:bg-slate-50/50">
                          <td className="px-3 py-2">
                            <Input
                              value={item.phase}
                              onChange={(e) => {
                                const items = [...newProject.cost_line_items];
                                items[idx] = { ...items[idx], phase: e.target.value };
                                setNewProject({ ...newProject, cost_line_items: items });
                              }}
                              placeholder="e.g., Water Softener Equipment"
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            {item.included_above ? (
                              <span className="text-xs text-slate-400 italic">Included above</span>
                            ) : (
                              <Input
                                type="number"
                                value={item.retail_price || ""}
                                onChange={(e) => {
                                  const items = [...newProject.cost_line_items];
                                  const retail = parseFloat(e.target.value) || 0;
                                  const sub = items[idx].subcontractor_cost || 0;
                                  items[idx] = { ...items[idx], retail_price: retail, projected_margin: retail - sub };
                                  setNewProject({ ...newProject, cost_line_items: items });
                                }}
                                placeholder="0"
                                className="h-8 text-sm text-right"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {item.included_above ? (
                              <span className="text-xs text-slate-400 italic">—</span>
                            ) : (
                              <Input
                                type="number"
                                value={item.subcontractor_cost || ""}
                                onChange={(e) => {
                                  const items = [...newProject.cost_line_items];
                                  const sub = parseFloat(e.target.value) || 0;
                                  const retail = items[idx].retail_price || 0;
                                  items[idx] = { ...items[idx], subcontractor_cost: sub, projected_margin: retail - sub };
                                  setNewProject({ ...newProject, cost_line_items: items });
                                }}
                                placeholder="0"
                                className="h-8 text-sm text-right"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {item.included_above ? (
                              <span className="text-xs text-slate-400">—</span>
                            ) : (
                              <span className={`text-sm font-semibold ${(item.projected_margin || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                ${(item.projected_margin || 0).toLocaleString()}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={item.contractor_team}
                              onChange={(e) => {
                                const items = [...newProject.cost_line_items];
                                items[idx] = { ...items[idx], contractor_team: e.target.value };
                                setNewProject({ ...newProject, cost_line_items: items });
                              }}
                              placeholder="e.g., RDX Plumbing"
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="px-1 py-2">
                            <button
                              type="button"
                              onClick={() => {
                                setNewProject({ ...newProject, cost_line_items: newProject.cost_line_items.filter((_, i) => i !== idx) });
                              }}
                              className="text-slate-400 hover:text-red-500 p-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-3 py-2 font-bold text-slate-700">TOTAL</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-700">
                          ${newProject.cost_line_items.filter(i => !i.included_above).reduce((sum, i) => sum + (i.retail_price || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-slate-700">
                          ${newProject.cost_line_items.filter(i => !i.included_above).reduce((sum, i) => sum + (i.subcontractor_cost || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-600">
                          ${newProject.cost_line_items.filter(i => !i.included_above).reduce((sum, i) => sum + (i.projected_margin || 0), 0).toLocaleString()}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Add line item buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="add-cost-line-item"
                  onClick={() => {
                    setNewProject({
                      ...newProject,
                      cost_line_items: [...newProject.cost_line_items, {
                        phase: "", retail_price: 0, subcontractor_cost: 0, projected_margin: 0, contractor_team: "", included_above: false
                      }]
                    });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Line Item
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewProject({
                      ...newProject,
                      cost_line_items: [...newProject.cost_line_items, {
                        phase: "", retail_price: 0, subcontractor_cost: 0, projected_margin: 0, contractor_team: "", included_above: true
                      }]
                    });
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add "Included Above" Item
                </Button>
                {newProject.cost_line_items.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    onClick={() => {
                      const totalRetail = newProject.cost_line_items.filter(i => !i.included_above).reduce((sum, i) => sum + (i.retail_price || 0), 0);
                      setNewProject({ ...newProject, budget: totalRetail });
                      toast.success(`Budget updated to $${totalRetail.toLocaleString()} from cost worksheet total`);
                    }}
                    data-testid="sync-budget-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-1" /> Sync Total to Budget
                  </Button>
                )}
              </div>
            </div>

            {/* Section 5: Project Team — All Stakeholders */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-2">
                <Users className="w-4 h-4" /> Project Team
              </h3>
              <p className="text-xs text-slate-400 mb-3">Add all contractors, suppliers, and hospital contacts so communication is available to all stakeholders from the start</p>

              {/* Team members list */}
              {newProject.team_members.length > 0 && (
                <div className="space-y-2 mb-4">
                  {newProject.team_members.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                        m.type === "contractor" ? "bg-blue-100 text-blue-700" :
                        m.type === "supplier" ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>
                        {m.type === "contractor" ? "C" : m.type === "supplier" ? "S" : "H"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{m.name} {m.organization ? `— ${m.organization}` : ""}</p>
                        <p className="text-xs text-slate-500">{m.role} | {m.email} {m.phone ? `| ${m.phone}` : ""}</p>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize flex-shrink-0">{m.type?.replace("_", " ")}</Badge>
                      <button type="button" onClick={() => {
                        setNewProject({ ...newProject, team_members: newProject.team_members.filter((_, i) => i !== idx) });
                      }} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick-add from existing contractors */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs">Add Contractor</Label>
                  <Select onValueChange={(v) => {
                    const c = contractors.find(c => c.id === v);
                    if (c && !newProject.team_members.find(m => m.email === c.email)) {
                      setNewProject({ ...newProject, team_members: [...newProject.team_members, {
                        name: c.name, email: c.email, phone: c.phone || "", role: "Contractor", organization: c.company_name, type: "contractor"
                      }]});
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select contractor..." /></SelectTrigger>
                    <SelectContent>
                      {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Add Supplier</Label>
                  <Select onValueChange={(v) => {
                    const s = suppliers.find(s => (s.id || s._id) === v);
                    if (s && !newProject.team_members.find(m => m.email === s.email)) {
                      setNewProject({ ...newProject, team_members: [...newProject.team_members, {
                        name: s.name, email: s.email, phone: s.phone || "", role: "Supplier", organization: s.company || "", type: "supplier"
                      }]});
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select supplier..." /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id || s._id} value={s.id || s._id}>{s.company || s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Manual add */}
              <details className="group">
                <summary className="text-xs text-blue-600 font-medium cursor-pointer hover:text-blue-700">
                  <UserPlus className="w-3.5 h-3.5 inline mr-1" />Add team member manually
                </summary>
                <div className="grid grid-cols-2 gap-3 mt-3 p-3 border rounded-lg bg-slate-50">
                  <Input placeholder="Full name" id="tm_name" />
                  <Input placeholder="Email" id="tm_email" type="email" />
                  <Input placeholder="Phone" id="tm_phone" />
                  <Input placeholder="Role (e.g., GC, Plumber, Infection Prevention)" id="tm_role" />
                  <Input placeholder="Organization" id="tm_org" />
                  <Select defaultValue="hospital_contact" onValueChange={(v) => document.getElementById("tm_type_val").value = v}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="supplier">Supplier</SelectItem>
                      <SelectItem value="hospital_contact">Hospital Contact</SelectItem>
                    </SelectContent>
                  </Select>
                  <input type="hidden" id="tm_type_val" defaultValue="hospital_contact" />
                  <div className="col-span-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const name = document.getElementById("tm_name").value;
                      const email = document.getElementById("tm_email").value;
                      if (!name || !email) { toast.error("Name and email are required"); return; }
                      setNewProject({ ...newProject, team_members: [...newProject.team_members, {
                        name, email,
                        phone: document.getElementById("tm_phone").value,
                        role: document.getElementById("tm_role").value || "Team Member",
                        organization: document.getElementById("tm_org").value,
                        type: document.getElementById("tm_type_val").value || "hospital_contact"
                      }]});
                      document.getElementById("tm_name").value = "";
                      document.getElementById("tm_email").value = "";
                      document.getElementById("tm_phone").value = "";
                      document.getElementById("tm_role").value = "";
                      document.getElementById("tm_org").value = "";
                    }}>
                      <Plus className="w-4 h-4 mr-1" /> Add to Team
                    </Button>
                  </div>
                </div>
              </details>
            </div>

            {/* Section 6: Compliance */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Compliance Requirements
              </h3>
              <div className="flex flex-wrap gap-2">
                {["HHSC", "Joint Commission", "ICRA", "OSHA", "ADA", "NFPA", "EPA", "AAMI ST108", "CMS"].map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const reqs = newProject.compliance_requirements || [];
                      setNewProject({
                        ...newProject,
                        compliance_requirements: reqs.includes(tag) ? reqs.filter(r => r !== tag) : [...reqs, tag]
                      });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      (newProject.compliance_requirements || []).includes(tag)
                        ? "bg-blue-100 text-blue-700 border-blue-300"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`compliance-tag-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNewProject(false)}>Cancel</Button>
            <Button onClick={handleCreateProject} className="bg-blue-600 hover:bg-blue-700" data-testid="create-project-submit">
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateSelect} onOpenChange={setShowTemplateSelect}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Choose a Project Template</DialogTitle>
            <DialogDescription>Start with a pre-configured project template with phases and compliance tags</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            {Object.entries(templates).map(([key, template]) => {
              const meta = {
                hospital_expansion: { icon: "bg-blue-100 text-blue-600", desc: "Full hospital expansion with HHSC review, construction, MEP, and commissioning" },
                dialysis_center: { icon: "bg-cyan-100 text-cyan-600", desc: "Dialysis center build-out including water system design and AAMI ST108 validation" },
                sterile_processing: { icon: "bg-violet-100 text-violet-600", desc: "SPD renovation with ICRA planning, HVAC, equipment install, and validation" },
                water_purification: { icon: "bg-sky-100 text-sky-600", desc: "RO, DI tanks, water softener install with full commissioning and water quality testing" },
              }[key] || { icon: "bg-slate-100 text-slate-600", desc: "" };
              return (
                <Card 
                  key={key} 
                  className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
                  onClick={() => handleCreateFromTemplate(key)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.icon}`}>
                        <FolderKanban className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800">{template.name}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{meta.desc}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge variant="outline" className="text-xs">{template.phases?.length || 0} phases</Badge>
                          <span className="text-xs text-slate-400">
                            ~{template.phases?.reduce((sum, p) => sum + (p.lead_time_weeks || 0), 0)} weeks total
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Phase Dialog — Comprehensive */}
      <Dialog open={showNewPhase} onOpenChange={setShowNewPhase}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Plus className="w-5 h-5 text-blue-600" />
              Add New Phase
            </DialogTitle>
            <DialogDescription>Define scope, team, compliance, and infection prevention for this phase</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Phase Basics */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Phase Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Phase Name *</Label>
                  <Input 
                    value={newPhase.name}
                    onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                    placeholder="e.g., Reverse Osmosis System Install"
                    data-testid="phase-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={newPhase.description}
                    onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
                    placeholder="What work will be performed in this phase?"
                    rows={2}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Scope Notes</Label>
                  <Textarea 
                    value={newPhase.scope_notes}
                    onChange={(e) => setNewPhase({ ...newPhase, scope_notes: e.target.value })}
                    placeholder="Detailed scope: materials, labor, specifications, deliverables..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Timeline & Budget */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Timeline & Budget
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={newPhase.start_date} onChange={(e) => setNewPhase({ ...newPhase, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={newPhase.end_date} onChange={(e) => setNewPhase({ ...newPhase, end_date: e.target.value })} />
                </div>
                <div>
                  <Label>Lead Time (weeks)</Label>
                  <Input type="number" value={newPhase.lead_time_weeks} onChange={(e) => setNewPhase({ ...newPhase, lead_time_weeks: parseInt(e.target.value) || 1 })} />
                </div>
                <div>
                  <Label>Budget ($)</Label>
                  <Input type="number" value={newPhase.budget} onChange={(e) => setNewPhase({ ...newPhase, budget: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={newPhase.status} onValueChange={(v) => setNewPhase({ ...newPhase, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Risk Level</Label>
                  <Select value={newPhase.risk_level} onValueChange={(v) => setNewPhase({ ...newPhase, risk_level: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(RISK_LEVELS).map(([key, config]) => (
                        <SelectItem key={key} value={key}><span style={{ color: config.color }}>{config.label}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Phase Contractors & Suppliers */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Contractors & Suppliers
              </h3>

              {/* Primary Owner */}
              <div className="mb-3">
                <Label className="text-xs">Primary Contractor (Owner)</Label>
                <Select value={newPhase.owner_id} onValueChange={(v) => {
                  if (v === "__add_contractor__") { setQuickAddFor("new_phase"); return; }
                  const c = contractors.find(c => c.id === v);
                  setNewPhase({ ...newPhase, owner_id: v, owner_name: c?.company_name || c?.name || "" });
                }}>
                  <SelectTrigger data-testid="new-phase-owner-select"><SelectValue placeholder="Assign primary contractor..." /></SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name || c.name}{c.documents_pending && " — docs pending"}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_contractor__" className="text-orange-600 font-semibold"
                      data-testid="new-phase-owner-add-new">+ Add new contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Assigned Contractors for this phase */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Additional Contractors</Label>
                  <Select onValueChange={(v) => {
                    const c = contractors.find(c => c.id === v);
                    if (c && !newPhase.assigned_contractors.find(a => a.id === v)) {
                      setNewPhase({ ...newPhase, assigned_contractors: [...newPhase.assigned_contractors, { id: c.id, name: c.name, company_name: c.company_name, role: "" }] });
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Add contractor..." /></SelectTrigger>
                    <SelectContent>
                      {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Suppliers for Materials</Label>
                  <Select onValueChange={(v) => {
                    const s = suppliers.find(s => (s.id || s._id) === v);
                    if (s && !newPhase.assigned_suppliers.find(a => a.id === v)) {
                      setNewPhase({ ...newPhase, assigned_suppliers: [...newPhase.assigned_suppliers, { id: s.id || s._id, name: s.name, company_name: s.company || "", materials: "" }] });
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Add supplier..." /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id || s._id} value={s.id || s._id}>{s.company || s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Assigned contractors/suppliers list */}
              {(newPhase.assigned_contractors.length > 0 || newPhase.assigned_suppliers.length > 0) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {newPhase.assigned_contractors.map((c, i) => (
                    <Badge key={`c-${i}`} className="bg-blue-50 text-blue-700 border-blue-200">
                      {c.company_name || c.name}
                      <button type="button" onClick={() => setNewPhase({ ...newPhase, assigned_contractors: newPhase.assigned_contractors.filter((_, idx) => idx !== i) })} className="ml-1 hover:text-red-500">&times;</button>
                    </Badge>
                  ))}
                  {newPhase.assigned_suppliers.map((s, i) => (
                    <Badge key={`s-${i}`} className="bg-amber-50 text-amber-700 border-amber-200">
                      {s.company_name || s.name}
                      <button type="button" onClick={() => setNewPhase({ ...newPhase, assigned_suppliers: newPhase.assigned_suppliers.filter((_, idx) => idx !== i) })} className="ml-1 hover:text-red-500">&times;</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Infection Prevention */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Infection Prevention
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>ICRA Classification</Label>
                  <Select 
                    value={newPhase.infection_prevention?.icra_class || ""} 
                    onValueChange={(v) => setNewPhase({ ...newPhase, infection_prevention: { ...newPhase.infection_prevention, icra_class: v } })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select ICRA class..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None Required</SelectItem>
                      <SelectItem value="class_i">Class I — Low Risk (minor maintenance)</SelectItem>
                      <SelectItem value="class_ii">Class II — Medium Risk (small-scale, short duration)</SelectItem>
                      <SelectItem value="class_iii">Class III — Medium-High Risk (work generates dust)</SelectItem>
                      <SelectItem value="class_iv">Class IV — High Risk (major construction, demolition)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Barriers</Label>
                  <Input 
                    value={newPhase.infection_prevention?.barriers || ""}
                    onChange={(e) => setNewPhase({ ...newPhase, infection_prevention: { ...newPhase.infection_prevention, barriers: e.target.value } })}
                    placeholder="e.g., Dust barriers, HEPA, negative pressure"
                  />
                </div>
                <div>
                  <Label>Monitoring Plan</Label>
                  <Input 
                    value={newPhase.infection_prevention?.monitoring || ""}
                    onChange={(e) => setNewPhase({ ...newPhase, infection_prevention: { ...newPhase.infection_prevention, monitoring: e.target.value } })}
                    placeholder="e.g., Daily air quality checks, surface sampling"
                  />
                </div>
                <div>
                  <Label>Precautions</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {["HEPA Filtration", "Negative Pressure", "Sealed Barriers", "Ante Room", "Sticky Mats", "PPE Required", "Traffic Control"].map(p => (
                      <button key={p} type="button" onClick={() => {
                        const prec = newPhase.infection_prevention?.precautions || [];
                        setNewPhase({ ...newPhase, infection_prevention: { ...newPhase.infection_prevention, precautions: prec.includes(p) ? prec.filter(x => x !== p) : [...prec, p] } });
                      }} className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                        (newPhase.infection_prevention?.precautions || []).includes(p)
                          ? "bg-red-50 text-red-700 border-red-300"
                          : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                      }`}>{p}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance Steps */}
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Compliance
              </h3>
              <div className="mb-3">
                <Label className="text-xs">Compliance Tags</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {["HHSC", "Joint Commission", "ICRA", "OSHA", "ADA", "NFPA", "EPA", "AAMI ST108", "CMS", "Local Permitting"].map(tag => (
                    <button key={tag} type="button" onClick={() => {
                      const tags = newPhase.compliance_tags || [];
                      setNewPhase({ ...newPhase, compliance_tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag] });
                    }} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      (newPhase.compliance_tags || []).includes(tag)
                        ? "bg-blue-100 text-blue-700 border-blue-300"
                        : "bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300"
                    }`}>{tag}</button>
                  ))}
                </div>
              </div>

              {/* Compliance checklist steps */}
              <div>
                <Label className="text-xs">Compliance Steps Checklist</Label>
                {newPhase.compliance_steps.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2 mt-2">
                    <Input 
                      value={step.step} 
                      onChange={(e) => {
                        const steps = [...newPhase.compliance_steps];
                        steps[idx] = { ...steps[idx], step: e.target.value };
                        setNewPhase({ ...newPhase, compliance_steps: steps });
                      }}
                      placeholder="Step description"
                      className="flex-1"
                    />
                    <Input 
                      value={step.responsible} 
                      onChange={(e) => {
                        const steps = [...newPhase.compliance_steps];
                        steps[idx] = { ...steps[idx], responsible: e.target.value };
                        setNewPhase({ ...newPhase, compliance_steps: steps });
                      }}
                      placeholder="Responsible party"
                      className="w-40"
                    />
                    <button type="button" onClick={() => setNewPhase({ ...newPhase, compliance_steps: newPhase.compliance_steps.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Button type="button" variant="ghost" size="sm" className="mt-2 text-blue-600" onClick={() => {
                  setNewPhase({ ...newPhase, compliance_steps: [...newPhase.compliance_steps, { step: "", responsible: "", status: "pending" }] });
                }}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Compliance Step
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNewPhase(false)}>Cancel</Button>
            <Button onClick={handleCreatePhase} className="bg-blue-600 hover:bg-blue-700" data-testid="add-phase-submit">
              <Plus className="w-4 h-4 mr-2" /> Add Phase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase Detail Dialog */}
      <Dialog open={!!showPhaseDetail} onOpenChange={() => setShowPhaseDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{showPhaseDetail?.name}</DialogTitle>
            <DialogDescription>View and manage phase details, updates, and status</DialogDescription>
          </DialogHeader>
          {showPhaseDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold">{showPhaseDetail.lead_time_weeks}w</p>
                  <p className="text-xs text-slate-500">Lead Time</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold">${(showPhaseDetail.budget || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Budget</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold">{showPhaseDetail.percent_complete || 0}%</p>
                  <p className="text-xs text-slate-500">Complete</p>
                </div>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <StatusBadge status={showPhaseDetail.status} />
                <RiskBadge level={showPhaseDetail.risk_level} />
                {showPhaseDetail.compliance_tags?.map(tag => (
                  <Badge key={tag} variant="outline">
                    <Shield className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
              
              {/* Updates Section */}
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  Updates
                </h4>
                <div className="max-h-48 overflow-y-auto space-y-2 mb-2">
                  {(showPhaseDetail.updates || []).map(update => (
                    <div key={update.id} className="bg-slate-50 p-3 rounded">
                      <p className="text-sm">{update.content}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {update.author_name} • {new Date(update.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                  {(!showPhaseDetail.updates || showPhaseDetail.updates.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-4">No updates yet</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Add an update..." 
                    value={newUpdate}
                    onChange={(e) => setNewUpdate(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddUpdate(showPhaseDetail.id)}
                  />
                  <Button onClick={() => handleAddUpdate(showPhaseDetail.id)}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick-add a contractor straight from a phase owner field */}
      <ContractorQuickAddDialog
        open={!!quickAddFor}
        onClose={() => setQuickAddFor(null)}
        onCreated={(c) => {
          setContractors(prev => [...prev, c]);
          if (quickAddFor === "new_phase") {
            setNewPhase(prev => ({ ...prev, owner_id: c.id, owner_name: c.company_name || c.name }));
          } else if (quickAddFor) {
            handleAssignContractorRecord(quickAddFor, c);
          }
          setQuickAddFor(null);
          fetchContractors();
        }}
      />

      {/* Pop-out editor for progress-report data entry */}
      <ExpandedEditorDialog
        open={!!editor}
        title={editor?.title || ""}
        subtitle={editor?.subtitle}
        value={editor?.value || ""}
        placeholder={editor?.placeholder}
        onSave={(text) => editor?.save?.(text)}
        onClose={() => setEditor(null)}
      />
    </div>
  );
}
